import WolfButton from '@components/ui/WolfButton';
import WolfTextLink from '@components/ui/WolfTextLink';
import { graphQlClient } from '@config/graphqlClient';
import ProcessesGraphQL from '@graphql/process.queries';
import ProcessExecutionGraphQL from '@graphql/processExecution.queries';
import WatchlistGraphQL from '@graphql/watchlist.queries';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { Discrepancy } from '@models/discrepancy.model';
import { Process, ProcessStatus, updateProcessStatusRequest } from '@models/process.model';
import { getLatestExecutionHistoryRequest } from '@models/processExecution.model';
import { isProcessWatchedRequest, watchProcessRequest, unwatchProcessRequest } from '@models/watchlist.model';
import { UserRoles } from '@models/user.model';
import { AssignmentIndOutlined, Bookmark, BookmarkBorder, CheckBoxOutlined, CloseOutlined, InfoOutlined, PlayArrow, PrintOutlined, ShareOutlined } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Icon, IconButton, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { RootState } from '@redux/store';
import trackUserActivity, { ActivityType } from '@utils/ai/trackUserActivity';
import { DateTime } from 'luxon';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import CustomModal from './CustomModal';
import ProcessDiscrepancyPanel from './ProcessDiscrepancyPanel';
import ProcessHeaderDropdown from './ProcessHeaderDropdown';
import { EditIcon24 } from './ui/WolfIcons';
import WolfInput from './ui/WolfInput';
import WolfTooltip from './ui/WolfTooltip';

interface ProcessHeaderProps {
  title: string;
  processId: string;
  location?: string;
  saving?: boolean;
  editMode: boolean;
  mode: 'edit' | 'view';
  showRemove?: boolean;
  origProcess: Process | undefined;
  discrepancies?: Discrepancy[];
  onSave: (addNewAfter?: boolean) => void;
  onCancel: () => void;
  disableCancel?: boolean;
  onDelete: () => void;
  onProcessDelete: () => void;
  onEdit: () => void;
  handleShowJsonImport: () => void;
  handleShowJsonExport: () => void;
  handleProcessUpdate: (process: Process) => void;
  handleProcessStatusUpdate?: (status: ProcessStatus) => void;
  handleCloneProcess: () => void;
  onDiscrepancyUpdate?: () => void;
  onToggleExecutionsTable?: () => void;
  showExecutionsTable?: boolean;
  isExecuting?: boolean;
  onStartExecution?: () => void;
  onCancelExecution?: () => void;
  onRequestExecution?: () => void;
}

export interface ProcessHeaderRef {
  fetchLastExecution: () => Promise<void>;
}

const ProcessHeader = React.forwardRef<ProcessHeaderRef, ProcessHeaderProps>(({
  title,
  processId,
  location,
  discrepancies,
  onDiscrepancyUpdate,
  saving,
  editMode,
  // mode, // unused, editMode is sufficient
  showRemove = true,
  origProcess,
  onSave,
  onCancel,
  disableCancel = false,
  onDelete,
  onProcessDelete,
  onEdit,
  handleShowJsonImport,
  handleShowJsonExport,
  handleProcessUpdate,
  handleProcessStatusUpdate,
  handleCloneProcess,
  onToggleExecutionsTable,
  showExecutionsTable = false,
  isExecuting = false,
  onStartExecution,
  onCancelExecution,
  onRequestExecution,
}, ref) => {
  const localeCommon = useTranslation('common');
  const localeActionMessages = useTranslation('actionMessages');
  const localeProcessForm = useTranslation('processForm');
  const localeProcesses = useTranslation('processes');
  const dispatch = useAppDispatch();

  const { role, rememberMe } = useAppSelector((state: RootState) => state.user);

  const [isHovering, setIsHovering] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(title);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [lastExecutionDate, setLastExecutionDate] = useState<string | null>(null);
  const [isWatched, setIsWatched] = useState(false);
  const [isWatchLoading, setIsWatchLoading] = useState(false);

  const history = useHistory();
  const locationHook = useLocation();
  const unblockRef = useRef<() => void>();
  const pendingLocationRef = useRef<any>(null);

  // Check if we should show discrepancies panel based on query parameter
  const searchParams = new URLSearchParams(locationHook.search);
  const shouldShowDiscrepancies = searchParams.get('showDiscrepancies') === 'true';

  const isDirty = tempTitle !== title && tempTitle !== undefined && isEditingTitle;

  // Sync tempTitle with title prop changes
  useEffect(() => {
    if (!isEditingTitle) {
      setTempTitle(title);
    }
  }, [title, isEditingTitle]);

  const fetchLastExecution = async () => {
    if (!processId) return;

    try {
      const response: getLatestExecutionHistoryRequest = await graphQlClient.request(
        ProcessExecutionGraphQL.queries.getLatestExecutionHistory,
        { processId },
      );

      if (response.getLatestExecutionHistory && response.getLatestExecutionHistory.length > 0) {
        // Get the most recent execution
        const sortedExecutions = response.getLatestExecutionHistory.sort(
          (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
        );
        setLastExecutionDate(sortedExecutions[0].startedAt);
      }
    } catch (error) {
      console.error('Error fetching execution history:', error);
    }
  };

  // Fetch last execution on mount
  useEffect(() => {
    fetchLastExecution();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processId]);

  // Check if process is watched
  const checkIsWatched = async () => {
    if (!processId) return;

    try {
      const response: isProcessWatchedRequest = await graphQlClient.request(
        WatchlistGraphQL.queries.isProcessWatched,
        { processId },
      );
      setIsWatched(response.isProcessWatched);
    } catch (error) {
      console.error('Error checking watch status:', error);
    }
  };

  // Check watch status on mount
  useEffect(() => {
    checkIsWatched();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processId]);

  // Handle watch/unwatch toggle
  const handleWatchToggle = async () => {
    if (!processId || isWatchLoading) return;

    setIsWatchLoading(true);
    try {
      if (isWatched) {
        await graphQlClient.request<unwatchProcessRequest>(
          WatchlistGraphQL.mutations.unwatchProcess,
          { processId },
        );
        setIsWatched(false);
        dispatch(
          appendActionMessage({
            message: localeProcesses['removedFromWatchlist'],
            type: SnackType.DARK,
          }),
        );
      } else {
        await graphQlClient.request<watchProcessRequest>(
          WatchlistGraphQL.mutations.watchProcess,
          { input: { processId } },
        );
        setIsWatched(true);
        dispatch(
          appendActionMessage({
            message: localeProcesses['addedToWatchlist'],
            type: SnackType.DARK,
          }),
        );
      }
    } catch (error) {
      console.error('Error toggling watch status:', error);
      dispatch(
        appendActionMessage({
          message: localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setIsWatchLoading(false);
    }
  };

  // Expose fetchLastExecution to parent component
  useImperativeHandle(ref, () => ({
    fetchLastExecution,
  }));

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    dispatch(appendActionMessage({ message: localeActionMessages['linkCopied'], type: SnackType.DARK }));
  };

  const handleSave = () => {
    onSave(false);
  };

  const handleTitleSubmit = () => {
    if (tempTitle !== title && tempTitle !== undefined && origProcess) {
      handleProcessUpdate({
        ...origProcess,
        name: tempTitle,
      });
    }
    setIsEditingTitle(false);
  };

  const handleTempTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempTitle(e.target.value);
  };

  const handleTitleEdit = () => {
    setTempTitle(title);
    setIsEditingTitle(true);
  };

  const handleTitleCancel = () => {
    setTempTitle(title);
    setIsEditingTitle(false);
  };

  const handleConfirmSaveAndLeave = () => {
    // Store pending location before clearing state
    const pendingLocation = pendingLocationRef.current;

    // Clear modal and editing state first
    setShowConfirmModal(false);
    setIsEditingTitle(false);
    pendingLocationRef.current = null;

    // Save the changes
    if (tempTitle !== title && tempTitle !== undefined && origProcess) {
      handleProcessUpdate({
        ...origProcess,
        name: tempTitle,
      });
    }

    // Continue with navigation after clearing dirty state
    if (pendingLocation) {
      // Use setTimeout to ensure state updates have processed
      setTimeout(() => {
        if (unblockRef.current) {
          unblockRef.current();
          unblockRef.current = undefined;
        }
        history.push(pendingLocation.pathname);
      }, 0);
    }
  };

  const handleCancelDiscard = () => {
    setShowConfirmModal(false);
    pendingLocationRef.current = null;
    // Don't change any other state - keep the editing state as is
  };

  const handleStatusChange = async (newStatus: ProcessStatus) => {
    if (!origProcess) return;

    setIsUpdatingStatus(true);

    // Store the original status for potential revert
    const originalStatus = origProcess.status;

    // Update the local state immediately (optimistic update)
    if (handleProcessStatusUpdate) {
      handleProcessStatusUpdate(newStatus);
    }

    try {
      const response: updateProcessStatusRequest = await graphQlClient.request(
        ProcessesGraphQL.mutations.updateProcessStatus,
        {
          processId: origProcess.processId,
          status: newStatus.toString(),
        },
      );

      if (response.updateProcessStatus) {
        if (newStatus === ProcessStatus.DONE) {
          await trackUserActivity(ActivityType.PROCESS_DONE_CHECK, rememberMe, processId);
          // Refresh process data after tracking activity to get any new discrepancies
          if (onDiscrepancyUpdate) {
            onDiscrepancyUpdate();
          }
          // Show notification that initial version will be created
          // Backend automatically creates v1.0 on first DONE status
          if (!origProcess.currentVersion) {
            dispatch(
              appendActionMessage({
                message: localeProcesses['versionCreated'].replace('{version}', '1.0'),
                type: SnackType.SUCCESS,
              }),
            );
          }
        }
      }
    } catch (error) {
      console.error('Error updating process status:', error);
      // Revert the optimistic update on error
      if (handleProcessStatusUpdate) {
        handleProcessStatusUpdate(originalStatus);
      }
      dispatch(
        appendActionMessage({
          message: localeActionMessages['processStatusUpdateError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleMarkExecution = () => {
    // Collapse execution history if it's currently open
    if (showExecutionsTable && onToggleExecutionsTable) {
      onToggleExecutionsTable();
    }
    // Start inline execution
    if (onStartExecution) {
      onStartExecution();
    }
  };

  const handlePrintPreview = () => {
    window.open(`/processes/${processId}/print`, '_blank');
  };

  const handleViewVersionHistory = () => {
    // Scroll to version history section
    const versionHistoryElement = document.getElementById('version-history');
    if (versionHistoryElement) {
      versionHistoryElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Navigation blocking effect
  useEffect(() => {
    const handleRouteChange = (location: any): string | false | void => {
      if (isDirty) {
        pendingLocationRef.current = location;
        setShowConfirmModal(true);
        return false; // Block the navigation
      }
      return undefined; // Allow navigation
    };

    if (isDirty) {
      unblockRef.current = history.block(handleRouteChange);
    } else {
      // Clean up the block when not dirty
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = undefined;
      }
    }

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
      }
    };
  }, [isDirty, history]);

  return (
    <>
      <Box className="flex justify-between items-center border-solid border-0 border-b border-primaryIcy p-4">
        <Box className="flex items-center gap-2">
          <Box
            className="flex items-center gap-2"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}>
            {!isEditingTitle && (
              <Box className="flex flex-col gap-1">
                <Box className="flex items-center gap-2">
                  <Typography variant="h3semibold">{title}</Typography>
                  {discrepancies && discrepancies.length > 0 && (
                    <ProcessDiscrepancyPanel
                      discrepancies={discrepancies}
                      onDiscrepancyUpdate={onDiscrepancyUpdate}
                      initiallyOpen={shouldShowDiscrepancies}
                    />
                  )}
                </Box>
                <Box className="flex items-start gap-2">
                  {origProcess && origProcess.lastUpdatedAt && (() => {
                    const isRecentlyUpdated = DateTime.fromISO(origProcess.lastUpdatedAt) > DateTime.now().minus({ months: 1 });
                    return (
                      <Box className="flex items-center gap-1">
                        <Typography variant="caption" sx={{ fontSize: '10px', color: 'text.secondary' }}>
                          {localeProcesses['updatedOn']}:{' '}
                          <span style={{ fontWeight: isRecentlyUpdated ? 600 : 400 }}>
                            {DateTime.fromISO(origProcess.lastUpdatedAt).toFormat('dd-MM-yyyy')}
                          </span>
                        </Typography>
                        {isRecentlyUpdated && (
                          <WolfTooltip title={localeProcesses['recentlyUpdatedTooltip']}>
                            <InfoOutlined sx={{ fontSize: 12, color: 'callToAction.main' }} />
                          </WolfTooltip>
                        )}
                      </Box>
                    );
                  })()}
                  {origProcess && origProcess.currentVersion && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '10px',
                        color: 'primary.main',
                        fontWeight: 600,
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                      onClick={handleViewVersionHistory}
                    >
                      {localeProcesses['version']}: v{origProcess.currentVersion}
                    </Typography>
                  )}
                </Box>
                {lastExecutionDate && (
                  <Box className="flex items-start gap-2">
                    <Typography variant="caption" sx={{ fontSize: '10px', color: 'text.secondary' }}>
                      {localeProcesses['lastExecution']}: {DateTime.fromISO(lastExecutionDate).toFormat('dd-MM-yyyy')}
                    </Typography>
                    <WolfTextLink
                      onClick={() => onToggleExecutionsTable && onToggleExecutionsTable()}
                      className="!text-[10px]">
                      {localeProcesses['viewExecutions']}
                    </WolfTextLink>
                  </Box>
                )}
              </Box>
            )}
            {isEditingTitle && (
              <Box className="flex items-center gap-2 min-w-96 w-96">
                <WolfInput label="" value={tempTitle} onChange={handleTempTitleChange} fullWidth boxFullWidth />
                <Box className="flex items-center gap-2">
                  <Icon onClick={handleTitleCancel} className="cursor-pointer p-0 text-sm" sx={{ color: 'error.main' }}>
                    <CloseOutlined />
                  </Icon>
                  <Icon
                    onClick={handleTitleSubmit}
                    className="cursor-pointer p-0 text-sm"
                    sx={{ color: 'success.main' }}>
                    <CheckBoxOutlined />
                  </Icon>
                </Box>
              </Box>
            )}
            {role !== UserRoles.USER && !isEditingTitle && (
              <Box className="flex items-center gap-2 w-5 h-5 ml-2">
                <Icon
                  onClick={handleTitleEdit}
                  className={`!text-sm !w-5 !h-5 transition-opacity duration-300 ${
                    isHovering ? 'opacity-100 cursor-pointer' : 'opacity-0 pointer-events-none'
                  }`}>
                  <EditIcon24 />
                </Icon>
              </Box>
            )}
          </Box>
        </Box>
        <Box className="flex items-center gap-2">
          {editMode && (
            <>
              {showRemove && (
                <WolfTextLink
                  onClick={onDelete}
                  color="error"
                  className={`!mr-3 ${saving ? '!text-gray-500 pointer-events-none' : ''}`}>
                  {localeProcessForm['removeStep']}
                </WolfTextLink>
              )}
              {disableCancel ? (
                <WolfTooltip title={localeProcessForm['firstStepRequired']} arrow>
                  <span>
                  <Box component="span" sx={{ display: 'inline-block' }}>
                    <WolfButton onClick={onCancel} variant="outlined" color="tertiary" disabled sx={{ pointerEvents: 'none' }}>
                      {localeCommon['cancel']}
                    </WolfButton>
                  </Box>
                  </span>
                </WolfTooltip>
              ) : (
                <WolfButton onClick={onCancel} variant="outlined" color="tertiary">
                  {localeCommon['cancel']}
                </WolfButton>
              )}
              <WolfButton onClick={handleSave} variant="contained" color="primary" disabled={saving}>
                {localeCommon['save']}
              </WolfButton>
            </>
          )}
          {!editMode && origProcess?.status === ProcessStatus.DONE && (
            <>
              <WolfButton
                variant={isExecuting ? 'outlined' : 'contained'}
                color={isExecuting ? 'error' : 'primary'}
                disabled={isUpdatingStatus}
                onClick={isExecuting ? onCancelExecution : onStartExecution}
                startIcon={<PlayArrow />}
                data-tutorial-id="execute-button">
                {isExecuting ? localeCommon['cancel'] : localeProcesses['markExecution']}
              </WolfButton>
              {!isExecuting && onRequestExecution && role !== UserRoles.USER && (
                <WolfButton
                  variant="outlined"
                  color="tertiary"
                  disabled={isUpdatingStatus}
                  onClick={onRequestExecution}
                  startIcon={<AssignmentIndOutlined />}
                  data-tutorial-id="request-execution-button">
                  {localeProcesses['requestExecution']}
                </WolfButton>
              )}
              {!isExecuting && (
                <WolfTooltip title={isWatched ? localeProcesses['removeFromWatchlist'] : localeProcesses['addToWatchlist']}>
                  <IconButton
                    onClick={handleWatchToggle}
                    disabled={isWatchLoading}
                    size="small"
                    sx={{ color: isWatched ? 'primary.main' : 'text.secondary' }}>
                    {isWatched ? <Bookmark /> : <BookmarkBorder />}
                  </IconButton>
                </WolfTooltip>
              )}
            </>
          )}
          {role !== UserRoles.USER && !editMode && origProcess?.status !== ProcessStatus.DONE && (
            <>
            {origProcess && origProcess.status === ProcessStatus.DRAFT && (
                <WolfTooltip
                  title={
                    localeCommon['status'] +
                    ': ' +
                    (origProcess.status?.charAt(0).toUpperCase() + origProcess.status?.slice(1).toLowerCase())
                  }>
                  <Box className="px-2 py-1 rounded-full text-sm font-medium text-primary bg-utilityPink">
                    {origProcess.status?.charAt(0).toUpperCase() + origProcess.status?.slice(1).toLowerCase()}
                  </Box>
                </WolfTooltip>
              )}
              <WolfButton
                variant="outlined"
                color="tertiary"
                disabled={isUpdatingStatus || isExecuting}
                onClick={() => handleStatusChange(ProcessStatus.DONE)}
                startIcon={<CheckCircleOutlineIcon />}>
                {localeProcesses['markAsApproved']}
              </WolfButton>
            </>
          )}
          {!editMode && origProcess?.status !== ProcessStatus.DONE && (
            <WolfButton variant="outlined" color="tertiary" onClick={onEdit} disabled={isExecuting}>
              {localeCommon['edit'] + ' ' + localeCommon['process']}
            </WolfButton>
          )}
          {!editMode && (
            <WolfTooltip title={localeCommon['share']}>
              <IconButton onClick={handleShare} size="small">
                <ShareOutlined />
              </IconButton>
            </WolfTooltip>
          )}
          {!editMode && (
            <WolfTooltip title={localeProcesses['printPreview']}>
              <IconButton onClick={handlePrintPreview} size="small" className="no-print">
                <PrintOutlined />
              </IconButton>
            </WolfTooltip>
          )}
          <ProcessHeaderDropdown
            location={location || ''}
            processStatus={origProcess?.status || ProcessStatus.DRAFT}
            onDelete={onProcessDelete}
            onEdit={onEdit}
            handleShowJsonImport={handleShowJsonImport}
            handleShowJsonExport={handleShowJsonExport}
            onStatusChange={handleStatusChange}
            handleCloneProcess={handleCloneProcess}
            handleShare={handleShare}
            handleMarkExecution={handleMarkExecution}
            handlePrintPreview={handlePrintPreview}
            handleViewVersionHistory={handleViewVersionHistory}
            hasVersions={!!origProcess?.currentVersion}
            isExecuting={isExecuting}
          />
        </Box>
      </Box>

      <CustomModal
        isOpen={showConfirmModal}
        title={localeCommon['confirm']}
        showButtons={true}
        maxWidth="600px"
        onClose={handleCancelDiscard}
        onConfirm={handleConfirmSaveAndLeave}
        customCancelText={localeCommon['cancelAndStayHere']}
        customConfirmText={localeCommon['saveAndLeave']}
        customConfirmColor="success">
        <Typography variant="body1">{localeCommon['changesNotSaved']}</Typography>
      </CustomModal>
    </>
  );
});

ProcessHeader.displayName = 'ProcessHeader';

export default ProcessHeader;
